import {
    Component, Input, forwardRef, ChangeDetectorRef, ChangeDetectionStrategy,
    OnInit, ViewChild, Output, EventEmitter, HostListener
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, ControlValueAccessor, FormControl } from '@angular/forms';
import { get, deepClone } from '../../../core/core';
import { MultiselectModel, multiValuesType, IMultiselectOptions, MultiselectOptions } from '../../model/multiselect.model';

const MULTISELECT_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MultiselectComponent),
    multi: true
};

const VALIDATOR_ACCESSOR = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => MultiselectComponent),
    multi: true,
}


@Component({
    selector: 'aria-multiselect',
    templateUrl: './multiselect.component.html',
    styleUrls: ['./multiselect.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MULTISELECT_ACCESSOR, VALIDATOR_ACCESSOR],
})
export class MultiselectComponent implements OnInit, ControlValueAccessor, Validator {

    @Input() placeholder: string;
    @Input() showLabel: boolean = false;
    @Input() options: IMultiselectOptions;
    // initialize label field

    @Input() selectList: Array<any> = [];
    @Input() disabled: boolean;
    @Input() name: string;
    @Input() index: number = 1;
    @Input() hasCounter: boolean = false;
    @Output() select: EventEmitter<any> = new EventEmitter();

    optionsList: Array<any>;
    openSelect: boolean = false;
    selectedOption: any = {};
    isOptionSelected: boolean = false;
    label: string;

    @ViewChild('selectInput', { static: true }) selectInput: any;

    private _value: multiValuesType;
    private _outputKey: string;
    private _labelKey: string;
    private _styleOnlyAuto: boolean;
    private _optionsFocusIndex: number = -1;
    private _model: MultiselectModel;

    constructor(private cdr: ChangeDetectorRef) {
        this.optionsList = [];
    }

    ngOnInit() {

        if (!this.selectList || !Array.isArray(this.selectList))
            this.selectList = [];

        if (this.options) {
            this.options = new MultiselectOptions(this.options);
            this._outputKey = this.options.outputKey;
            this._labelKey = this.options.labelKey;
            this._styleOnlyAuto = this.options.styleOnlyAuto;
            this.optionsList = deepClone(this.selectList);
        } else {
            this._labelKey = 'id';
            this.optionsList = deepClone(this.selectList).map((val) => ({
                id: val
            }));
        }
    }

    //SETTERS / GETTERS

    get value(): any {
        return this._value;
    };

    set value(v: any) {
        if (v !== this._value) {
            this._value = v;

            this.cdr.markForCheck();
        }
    }

    get labelKey() {
        return this._labelKey;
    }

    get outputKey() {
        return this._outputKey;
    }



    @HostListener('keypress', ['$event']) onKeyPress(e) {
        this.preventDefault(e);
    }

    @HostListener('keydown', ['$event']) onKeyDown(e) {

        if (!this.openSelect || (this.openSelect && !this.optionsList.length)) return;

        if (e.key == 'Tab') {
            this.openSelect = false;
            return;
        }

        if (!/ArrowUp|ArrowDown/.test(e.key)) {
            return;
        };

        const keyName = e.key;

        if (this._optionsFocusIndex == -1) {
            this._optionsFocusIndex = 0;
        } else if (keyName == 'ArrowUp') {
            this._optionsFocusIndex--;
        } else if (keyName == 'ArrowDown') {
            if (this._optionsFocusIndex != this.optionsList.length - 1)
                this._optionsFocusIndex++;
        }

        if (this._optionsFocusIndex == -1) {
            this.selectInput.nativeElement.focus();
        } else {
            const currentElement = document.getElementById(`selectOpt-${this._optionsFocusIndex}`);
            if (currentElement) currentElement.focus();
        }
    }

    /**
     * Set value is used to setup output ngModel value
     * @param value string or number to change
     */
    setValue(value: multiValuesType) {
        this.value = value;
        this.onChange(value)
    }

    /**
     * get value from select option by options outputKey
     * @param option option from select
     */
    getOptionValue(option: any) {
        return this._outputKey ? get(option, this._outputKey) : option;
    }

    /**
     * get label from select option by options labelKey
     * @param option option from select
     */
    getOptionLabel(option: any) {
        if (typeof option !== 'string') {
            return this._labelKey ? get(option, this._labelKey) : this.getOptionValue(option);
        } else {
            return option;
        }
    }

    /**
     * Reset multiselect model on X click
     */
    resetModel() {
        this._model.reset();
        this.label = this._model.label;
        this.setValue([]);
        this.optionsList.forEach((option) => option.isSelected = false)
    }

    /**
     * Select option from select list
     * @param option abject or string to select
     */
    selectOption(option: any) {
        option.isSelected = !option.isSelected;

        if (option.isSelected) {
            this._model.addSelection({
                value: this.getOptionValue(option),
                label: this.getOptionLabel(option)
            });
        } else {
            this._model.removeSelection(this.getOptionValue(option))
        }

        this.label = this._model.label;
        this.setValue(this._model.selectionsIds);
        this.selectedOption = this._styleOnlyAuto ? {} : option;

        this.select.emit(this._model.selections);

        this.cdr.markForCheck();
    }

    /**
     * On input focus open select or do the first call to retrieve data and than open select
     * @param value current value of input itself
     */
    onFocus(value) {
        this.openSelect = true;
        this._optionsFocusIndex = -1;
    }


    /**
     * On click outside autocomplete div or input close autocomplete
     * @param event click event
     */
    onBlur(event) {

        if (!event.relatedTarget || !/selectOpt-\d*|ms-checkbox-\d*/.test(event.relatedTarget.id)) {
            this.openSelect = false;
        }

    }

    preventDefault(event) {
        event.preventDefault()
    }


    // REQURED METHODS FOR NGMODEL DESIGN PATTERN

    public validate(c: FormControl) {
        let errors = { ...c.errors }, hasError = false;

        if (this.label && !this.value) {
            hasError = true;
            errors['noSelectionError'] = { valid: false }
        }

        if (hasError || c.invalid) {
            return errors;
        } else {
            return null;
        }
    }

    /**
     * Reuired from ngModel design pattern to work
     * @param value initial value
     */
    writeValue(values: any[]): void {
        if (values && Array.isArray(values) && this.value != values) {

            this._model = new MultiselectModel([], this.hasCounter);
            const currentKey = this._outputKey || this._labelKey;

            values.forEach((selection) => {
                const currentOption = this.optionsList.find((option) => option[currentKey] == selection);
                if (currentOption) {
                    currentOption.isSelected = true;
                    this._model.addSelection({
                        value: this.getOptionValue(currentOption),
                        label: this.getOptionLabel(currentOption)
                    })
                }
            })
            this.label = this._model.label;
            this.setValue(values);

            this.cdr.markForCheck();
        }
    }

    onChange = (_) => { };
    onTouched = () => { };

    registerOnChange(fn: (_: any) => void): void { this.onChange = fn; }
    registerOnTouched(fn: () => void): void { this.onTouched = fn; }

}